import { useState, useCallback, useMemo } from 'react'
import { tourProgressService } from '../services/tourService'
import { useAuthStore } from '@/stores/authStore'

export function useTourProgress() {
  // Progress is stored per user, so it is re-read whenever the signed-in user changes.
  // Deriving it in render (not in an effect) means the first render for a user already
  // sees their saved progress; an effect would show one stale empty render, which the
  // first-visit auto-start would read as "never seen the tour".
  const userId = useAuthStore((s) => s.user?.id)
  const [version, setVersion] = useState(0)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- userId and version are the cache keys
  const progress = useMemo(() => tourProgressService.load(), [userId, version])
  const refresh = useCallback(() => setVersion((v) => v + 1), [])

  const markCompleted = useCallback((tourId: string) => {
    tourProgressService.markCompleted(tourId)
    refresh()
  }, [refresh])

  const markStarted = useCallback((tourId: string) => {
    tourProgressService.markStarted(tourId)
    refresh()
  }, [refresh])

  const resetProgress = useCallback(() => {
    tourProgressService.reset()
    refresh()
  }, [refresh])

  const isCompleted = useCallback((tourId: string) => {
    return progress[tourId]?.completed === true
  }, [progress])

  const completedCount = Object.values(progress).filter((p) => p.completed).length

  return {
    progress,
    markCompleted,
    markStarted,
    resetProgress,
    isCompleted,
    completedCount,
  }
}
